import { useEffect, useRef } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Grid,
  Input,
  Line,
  Picker,
  RadioGroup,
  Row,
  SelectForm,
  SimpleCollapse
} from 'components'
import {
  DraggableDialog,
  resetModalState
} from 'components/DraggableDialog/DraggableDialog'
import Draggable from 'react-draggable'
import { Control, useForm } from 'react-hook-form'
import {
  EfetivoLogicoServices,
  EffectiveData,
  ResGetAllContracts
} from 'services/Supplier/LogicalEffective'
import { buscaCep } from 'services/Utils/buscaCep'
import { FORMAT, SPACING, STATES } from 'utils'

import { DadosEmpresa } from '../../../RegisterEffectives/components'
import { SCHEMA } from '../../../yup/schema'
import { DialogTitleStyled, FormDialog } from './EditEffectiveModal.style'

export interface RegisterEffectivesProps {
  contractData: ResGetAllContracts
  showModal: boolean
  toggleModal: () => void
  handleSubmitUpdate: any
  effectiveData: EffectiveData
}

const EditEffectiveModal: React.FC<RegisterEffectivesProps> = ({
  contractData,
  showModal,
  toggleModal,
  handleSubmitUpdate,
  effectiveData
}) => {
  const draggableReference = useRef<Draggable>(null)
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm<EffectiveData>({
    resolver: yupResolver(SCHEMA.logicalEffectiveForm)
  })

  const form = watch()

  const genderValue = watch('sgGenero')
  const txNacionalidade = watch('txNacionalidade')
  const sgEstadoDocto = watch('sgEstadoDocto')
  const sgEstadoCtps = watch('sgEstadoCtps')
  const sgEstadoEnd = watch('sgEstadoEnd')
  const cdCep = watch('cdCep')
  const orgaoEmissor = watch('txEmissorDocto')
  const endereco = watch('txEndereco')

  const statesList = STATES.map((state) => ({ label: state, value: state }))

  const subtitleCollapse = `Matrícula: ${effectiveData?.txMatricula}`

  const handleChangeOrgaoEmissor = () => {
    const newValue = form?.txEmissorDocto
      .replace(/[^A-Za-z]/g, '')
      .toLocaleUpperCase()
    setValue('txEmissorDocto', newValue)
  }

  const setInitialValues = () => {
    setValue('cdCpf', effectiveData?.cdCpf)
    setValue('nmColaborador', effectiveData?.nmColaborador)
    setValue('txFuncao', effectiveData?.txFuncao)
    setValue('usaRefeitorio', effectiveData?.usaRefeitorio)
    setValue('txFiliacaoPai', effectiveData?.txFiliacaoPai)
    setValue('txFiliacaoMae', effectiveData?.txFiliacaoMae)
    setValue('dtNascimento', effectiveData?.dtNascimento)
    setValue('sgGenero', effectiveData?.sgGenero)
    setValue('txNacionalidade', effectiveData?.txNacionalidade)
    setValue('txNaturalidade', effectiveData?.txNaturalidade)
    setValue('cdDocumento', effectiveData?.cdDocumento ?? undefined)
    setValue('txEmissorDocto', effectiveData?.txEmissorDocto ?? undefined)
    setValue('sgEstadoDocto', effectiveData?.sgEstadoDocto ?? undefined)
    setValue('cdCtps', effectiveData?.cdCtps ?? undefined)
    setValue('cdSerieCtps', effectiveData?.cdSerieCtps ?? undefined)
    setValue('sgEstadoCtps', effectiveData?.sgEstadoCtps ?? undefined)
    setValue('cdPis', effectiveData?.cdPis ?? undefined)
    setValue('cdCnh', effectiveData?.cdCnh)
    setValue('txEndereco', effectiveData?.txEndereco ?? undefined)
    setValue('nuNumero', effectiveData?.nuNumero ?? undefined)
    setValue('txComplemento', effectiveData?.txComplemento)
    setValue('txBairro', effectiveData?.txBairro ?? undefined)
    setValue('txCidade', effectiveData?.txCidade ?? undefined)
    setValue('txLocalAcesso', effectiveData?.txLocalAcesso)
    setValue('cdCep', effectiveData?.cdCep ? 
      effectiveData?.cdCep.split(' ').join('') : undefined)
    setValue('txEndereco', effectiveData?.txEndereco ?? undefined)
    setValue('txBairro', effectiveData?.txBairro ?? undefined)
    setValue('txCidade', effectiveData?.txCidade ?? undefined)
    setValue('sgEstadoEnd', effectiveData?.sgEstadoEnd ?? undefined)
  }

  useEffect(() => {
    effectiveData && setInitialValues()
  }, [effectiveData])

  useEffect(() => {
    reset(
      {
        sgEstadoEnd: '',
        txBairro: '',
        txCidade: '',
        txEndereco: ''
      },
      {
        keepValues: true
      }
    )
    setValue('txEndereco', '')
    setValue('txBairro', '')
    setValue('txCidade', '')
    setValue('sgEstadoEnd', '')
    if (cdCep?.length > 7) {
      buscaCep(cdCep).then(({ data }) => {
        reset(
          {
            sgEstadoEnd: '',
            txBairro: '',
            txCidade: '',
            txEndereco: ''
          },
          {
            keepValues: true
          }
        )
        setValue('txEndereco', data?.logradouro)
        setValue('txBairro', data?.bairro)
        setValue('txCidade', data?.localidade)
        setValue('sgEstadoEnd', data?.uf)
      })
    }
  }, [cdCep])

  useEffect(() => {
    if (cdCep?.length > 0 && !endereco) {
      setError('cdCep', { message: 'CEP inválido' })
    } else {
      clearErrors('cdCep')
    }
  }, [endereco, cdCep])

  useEffect(() => {
    if (orgaoEmissor) handleChangeOrgaoEmissor()
  }, [orgaoEmissor])

  return (
    <DraggableDialog
      showModal={showModal}
      reference={draggableReference}
      onClose={toggleModal}
      width={'xl'}
    >
      <FormDialog
        onSubmit={handleSubmit((data) => {
          handleSubmitUpdate({
            ...data,
            idEfetivoLogico: effectiveData.idEfetivoLogico,
            txMatricula: effectiveData.txMatricula
          })
          resetModalState(draggableReference)
        })}
      >
        <Line />
        <DialogTitleStyled>Editar Efetivo Lógico</DialogTitleStyled>
        <DadosEmpresa contractData={contractData} />
        <>
          <SimpleCollapse
            title="DADOS DO COLABORADOR"
            subtitle={subtitleCollapse}
            isExpanded
          >
            <Grid
              gridCols="2.1fr 2fr 2fr 2fr"
              gridGap="1rem"
              padding={`0 0 ${SPACING.spacing30}`}
            >
              <Input
                control={control}
                errors={errors}
                label="CPF / Passaporte"
                placeholder="Documento do Colaborador"
                formValue="cdCpf"
                disabled
                mask={
                  isNaN(parseInt(form.cdCpf))
                    ? ['NN###########']
                    : ['###.###.###-##']
                }
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Colaborador"
                placeholder="Nome do Empregado"
                formValue="nmColaborador"
                maxLength={50}
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Função"
                placeholder="Nome da Função"
                formValue="txFuncao"
                maxLength={50}
                required
              />
              <RadioGroup
                control={control}
                label="Refeitório (Opcional)"
                row
                formValue="usaRefeitorio"
                defaultValue={false}
                listOptions={[
                  { label: 'Sim', value: true },
                  { label: 'Não', value: false }
                ]}
              />
            </Grid>
            <Grid
              gridCols="2.1fr 2fr 2fr 2fr"
              gridGap="1rem"
              padding={`0 0 ${SPACING.spacing30}`}
            >
              <Input
                control={control}
                errors={errors}
                label="Pai do Colaborador"
                placeholder="Nome do Pai"
                formValue="txFiliacaoPai"
                maxLength={50}
              />
              <Input
                control={control}
                errors={errors}
                label="Mãe do Colaborador"
                placeholder="Nome da Mãe"
                formValue="txFiliacaoMae"
                maxLength={50}
                required
              />
            </Grid>
            <Grid
              gridCols="1fr 1fr 2fr 2fr 2fr"
              gridGap="1rem"
              padding={`0 0 ${SPACING.spacing30}`}
            >
              <Picker
                control={control as Control<any, any>}
                errors={errors}
                formValue="dtNascimento"
                label="Data de Nascimento"
              />
              <SelectForm
                defaultValue={genderValue}
                errors={errors}
                control={control}
                formValue="sgGenero"
                required
                listOptions={[
                  { label: 'Masculino', value: 'M' },
                  { label: 'Feminino', value: 'F' }
                ]}
                label="Gênero"
              />
              <SelectForm
                defaultValue={txNacionalidade}
                control={control}
                errors={errors}
                formValue="txNacionalidade"
                required
                listOptions={[
                  { label: 'Brasileiro', value: 'Brasileiro' },
                  { label: 'Estrangeiro', value: 'Estrangeiro' }
                ]}
                label="Nacionalidade"
              />
              <Input
                control={control}
                errors={errors}
                label="Naturalidade (Opcional)"
                placeholder="Local de Nascimento"
                formValue="txNaturalidade"
                maxLength={20}
              />
            </Grid>
            <Grid
              gridCols="2.1fr 0.9fr 1fr 2fr 2fr"
              gridGap="1rem"
              padding={`0 0 ${SPACING.spacing30}`}
            >
              <Input
                control={control}
                errors={errors}
                label="RG"
                placeholder="RG do Colaborador"
                formValue="cdDocumento"
                //mask={['##.###.###-##']}
                maxLength={15}
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Órgão"
                placeholder="Órgão Emissor"
                formValue="txEmissorDocto"
                maxLength={7}
                required
              />
              <SelectForm
                defaultValue={sgEstadoDocto}
                control={control}
                errors={errors}
                formValue="sgEstadoDocto"
                listOptions={statesList}
                label="UF"
              />
            </Grid>
            <Grid
              gridCols="2.1fr 0.9fr 1fr 2fr 2fr"
              gridGap="1rem"
              padding={`0 0 ${SPACING.spacing30}`}
            >
              <Input
                control={control}
                errors={errors}
                label="CTPS"
                placeholder="Nº"
                formValue="cdCtps"
                mask={['#############']}
                maxLength={13}
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Série"
                placeholder="Nº"
                formValue="cdSerieCtps"
                mask={['####']}
                maxLength={4}
                required
              />
              <SelectForm
                defaultValue={sgEstadoCtps}
                control={control}
                errors={errors}
                formValue="sgEstadoCtps"
                listOptions={statesList}
                label="UF"
                required
              />
              <Input
                control={control}
                errors={errors}
                label="PIS"
                placeholder="Nº"
                formValue="cdPis"
                mask={['###.#####.##-#']}
                maxLength={14}
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Prontuário (CNH) (Opcional)"
                placeholder="Nº"
                formValue="cdCnh"
                mask={['#########-##']}
                maxLength={12}
              />
            </Grid>
            <Grid
              gridCols="1fr 4fr 1fr 2fr"
              gridGap="1rem"
              padding={`0 0 ${SPACING.spacing30}`}
            >
              <Input
                control={control}
                errors={errors}
                label="CEP"
                formValue="cdCep"
                placeholder="00000-000"
                mask={['#####-###']}
                maxLength={9}
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Endereço"
                placeholder="Logradouro"
                formValue="txEndereco"
                maxLength={50}
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Número (Opcional)"
                placeholder="Nº do Logradouro"
                formValue="nuNumero"
                maxLength={4}
              />
              <Input
                control={control}
                errors={errors}
                label="Complemento"
                placeholder="Ex. Casa/Fundos"
                formValue="txComplemento"
                maxLength={50}
              />
            </Grid>
            <Grid
              gridCols="2.5fr 2.5fr 1fr 2fr"
              gridGap="1rem"
              padding={`0 0 ${SPACING.spacing30}`}
            >
              <Input
                control={control}
                errors={errors}
                label="Bairro"
                placeholder="Bairro"
                formValue="txBairro"
                maxLength={40}
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Cidade"
                placeholder="Cidade"
                formValue="txCidade"
                maxLength={50}
                required
              />
              <SelectForm
                defaultValue={sgEstadoEnd}
                control={control}
                errors={errors}
                formValue="sgEstadoEnd"
                listOptions={statesList}
                label="UF"
                required
              />
              <Input
                control={control}
                errors={errors}
                label="Local de Acesso (Opcional)"
                placeholder="Local de Acesso"
                formValue="txLocalAcesso"
                maxLength={50}
              />
            </Grid>
          </SimpleCollapse>
          <Row
            justifyContent="flex-end"
            marginTop={SPACING.spacing30}
            marginBottom={SPACING.spacing20}
          >
            <Button
              text="Cancelar"
              variantButton="outlined"
              onClick={() => {
                reset()
                toggleModal()
              }}
              marginRight={SPACING.spacing20}
              marginBottom={SPACING.spacing10}
            />
            <Button
              text="Salvar"
              type="submit"
              marginRight={SPACING.spacing20}
              marginBottom={SPACING.spacing10}
            />
          </Row>
        </>
      </FormDialog>
    </DraggableDialog>
  )
}

export default EditEffectiveModal
